'use client';

/* eslint-disable no-unused-vars */

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowRight,
  Bot,
  Check,
  CheckCircle2,
  Clock3,
  Crown,
  Loader2,
  LogIn,
  Menu,
  MessageSquarePlus,
  Pencil,
  Search,
  Send,
  Sparkles,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';

import styles from '@/app/[locale]/ai-model/ai-models.module.css';
import TurnstileWidget from '@/components/TurnstileWidget';
import AiChatModelUpgradeDialog from '@/components/ai-models/AiChatModelUpgradeDialog';
import { AI_CHAT_TURNSTILE_ACTION, type AiChatMessage } from '@/libs/ai-chat-shared';
import { getAiChatConversationCopy, getAiChatCopy, type AiChatCopy } from '@/libs/ai-chat-i18n';
import { getAiChatModelAccessCopy } from '@/libs/ai-chat-model-access-i18n';
import {
  AI_CHAT_TEXT_MODELS,
  DEFAULT_FREE_AI_CHAT_MODEL_ID,
  getAiChatModel,
  groupAiChatModelsByOrganization,
  isAiChatModelAvailableForPlan,
  isAiChatModelId,
  type AiChatModelId,
} from '@/libs/ai-chat-models';

type QuotaState = {
  authenticated: boolean;
  subscribed: boolean;
  plan: 'standard' | 'plus' | 'pro';
  usagePercent: number;
  exhausted: boolean;
  resetAt: string | null;
  humanVerification: {
    required: boolean;
    verified: boolean;
    expiresAt: string | null;
  };
};

type ConversationSummary = {
  id: string;
  title: string;
  model: AiChatModelId;
  preview: string | null;
  lastMessageAt: string;
  createdAt: string;
  updatedAt: string;
};

type Props = {
  locale: string;
  turnstileSiteKey: string;
  turnstileMode: 'message' | 'session';
  defaultModelId: AiChatModelId;
  plusMonthlyPriceUsd: number;
  proMonthlyPriceUsd: number;
};

const TURNSTILE_INTRO_SESSION_KEY = 'aitoolly:ai-chat:turnstile-intro';
const LOW_QUOTA_SESSION_KEY = 'aitoolly:ai-chat:low-quota';
const HUMAN_VERIFICATION_RETURN_KEY = 'aitoolly:ai-chat:verification-return';
const PENDING_CHAT_REQUEST_SESSION_KEY = 'aitoolly:ai-chat:pending-request';

type VerificationReturnState = {
  draft: string;
  model: AiChatModelId;
  conversationId: string | null;
};

type PendingChatRequest = {
  requestId: string;
  conversationId: string;
  model: AiChatModelId;
  userContent: string;
  messages: AiChatMessage[];
};

function errorCopy(code: string | undefined, copy: AiChatCopy) {
  if (code === 'DAILY_LIMIT_REACHED') return copy.quotaReached;
  if (code === 'AI_CHAT_NOT_CONFIGURED' || code === 'AI_CHAT_DATABASE_NOT_READY') return copy.configurationError;
  if (code === 'TURNSTILE_FAILED') return copy.humanExpired;
  if (code === 'AI_CHAT_RATE_LIMITED') return copy.rateLimited;
  if (code === 'AI_CHAT_MODEL_BUDGET_TOO_LOW') return copy.modelBudgetLow;
  if (code === 'HUMAN_VERIFICATION_REQUIRED') return copy.humanNeeded;
  return copy.generalError;
}

function groupKey(value: string) {
  const date = new Date(value);
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.floor((startToday - startDate) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days <= 7) return 'previousWeek';
  return 'older';
}

function UsageMeter({ label, resetLabel, percent, compact = false }: { label: string; resetLabel: string; percent: number; compact?: boolean }) {
  return (
    <div className={`${styles.chatUsageMeter} ${compact ? styles.chatUsageMeterCompact : ''}`} aria-label={`${label}. ${resetLabel}`}>
      <span className={styles.chatUsageHeader}>
        <strong>{label}</strong>
        {!compact && <small>{resetLabel}</small>}
      </span>
      <span className={styles.chatUsageTrack} aria-hidden="true"><i style={{ width: `${percent}%` }} /></span>
    </div>
  );
}

export default function AiChatClient({
  locale,
  turnstileSiteKey,
  turnstileMode,
  defaultModelId,
  plusMonthlyPriceUsd,
  proMonthlyPriceUsd,
}: Props) {
  const copy = getAiChatCopy(locale, turnstileMode);
  const modelAccessCopy = getAiChatModelAccessCopy(locale);
  const conversationCopy = getAiChatConversationCopy(locale);
  const searchParams = useSearchParams();
  const [quota, setQuota] = useState<QuotaState | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(true);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [selectedModelId, setSelectedModelId] = useState<AiChatModelId>(defaultModelId);
  const [draft, setDraft] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstilePreferenceReady, setTurnstilePreferenceReady] = useState(false);
  const [turnstileIntroductionComplete, setTurnstileIntroductionComplete] = useState(false);
  const [turnstileSilentMode, setTurnstileSilentMode] = useState(false);
  const [turnstileInteractionRequired, setTurnstileInteractionRequired] = useState(false);
  const [turnstileRenderCycle, setTurnstileRenderCycle] = useState(0);
  const [turnstileStatus, setTurnstileStatus] = useState<'waiting' | 'ready' | 'expired'>('waiting');
  const [sending, setSending] = useState(false);
  const [syncPendingRequest, setSyncPendingRequest] = useState<PendingChatRequest | null>(null);
  const [lowQuotaOpen, setLowQuotaOpen] = useState(false);
  const [upgradeModelId, setUpgradeModelId] = useState<AiChatModelId | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const turnstileIntroductionCompleteRef = useRef(false);
  const pendingVerificationReturn = useRef<VerificationReturnState | null>(null);
  const pendingChatRequest = useRef<PendingChatRequest | null>(null);
  const sendingRef = useRef(false);
  const messagesEnd = useRef<HTMLDivElement>(null);
  const conversationRequest = useRef(0);
  const selectedModelRef = useRef<AiChatModelId>(defaultModelId);

  const chatPath = locale === 'en' ? '/ai-chat' : `/${locale}/ai-chat`;
  const pricingPath = locale === 'en' ? '/pricing' : `/${locale}/pricing`;
  const chatPricingPath = `${pricingPath}#ai-chat-plans`;
  const dashboardPath = locale === 'en' ? '/dashboard' : `/${locale}/dashboard`;
  const signInPath = `${locale === 'en' ? '/signin' : `/${locale}/signin`}?returnTo=${encodeURIComponent(chatPath)}`;
  const verifyPath = `${locale === 'en' ? '/signin/verify' : `/${locale}/signin/verify`}?returnTo=${encodeURIComponent(chatPath)}`;
  const isMessageTurnstile = turnstileMode === 'message';

  const rememberPendingChatRequest = useCallback((pending: PendingChatRequest | null) => {
    pendingChatRequest.current = pending;
    try {
      if (pending) window.sessionStorage.setItem(PENDING_CHAT_REQUEST_SESSION_KEY, JSON.stringify(pending));
      else window.sessionStorage.removeItem(PENDING_CHAT_REQUEST_SESSION_KEY);
    } catch {
      // The in-memory request ID still protects retries while this page remains open.
    }
  }, []);
  const activeConversation = useMemo(
    () => conversations.find((conversation) => conversation.id === activeConversationId) || null,
    [activeConversationId, conversations],
  );
  const availableModelId = useCallback((value: unknown): AiChatModelId => {
    const model = getAiChatModel(value);
    if (!model) return quota?.plan === 'standard' ? DEFAULT_FREE_AI_CHAT_MODEL_ID : defaultModelId;
    return isAiChatModelAvailableForPlan(model, quota?.plan || 'standard') ? model.id : DEFAULT_FREE_AI_CHAT_MODEL_ID;
  }, [defaultModelId, quota?.plan]);

  const redirectForHumanVerification = useCallback((state?: Partial<VerificationReturnState>) => {
    const pending: VerificationReturnState = {
      draft: state?.draft ?? draft,
      model: state?.model ?? selectedModelId,
      conversationId: state?.conversationId === undefined ? activeConversationId : state.conversationId,
    };
    try {
      window.sessionStorage.setItem(HUMAN_VERIFICATION_RETURN_KEY, JSON.stringify(pending));
    } catch {
      // Verification still proceeds when session storage is unavailable.
    }
    window.location.assign(verifyPath);
  }, [activeConversationId, draft, selectedModelId, verifyPath]);

  const loadQuota = useCallback(async () => {
    setQuotaLoading(true);
    try {
      const response = await fetch('/api/ai-chat', { cache: 'no-store' });
      const payload = await response.json().catch(() => null);
      if (response.status === 401) {
        setQuota({
          authenticated: false,
          subscribed: false,
          plan: 'standard',
          usagePercent: 0,
          exhausted: false,
          resetAt: null,
          humanVerification: { required: turnstileMode === 'session', verified: false, expiresAt: null },
        });
      } else if (response.ok) {
        const nextQuota = payload as QuotaState;
        const currentModel = AI_CHAT_TEXT_MODELS.find((model) => model.id === selectedModelRef.current);
        if (nextQuota.plan === 'standard' && currentModel && !isAiChatModelAvailableForPlan(currentModel, nextQuota.plan)) {
          selectedModelRef.current = DEFAULT_FREE_AI_CHAT_MODEL_ID;
          setSelectedModelId(DEFAULT_FREE_AI_CHAT_MODEL_ID);
        }
        setQuota(nextQuota);
      } else {
        setQuota(null);
        setError(copy.loadError);
      }
    } catch {
      setQuota(null);
      setError(copy.loadError);
    } finally {
      setQuotaLoading(false);
    }
  }, [copy.loadError, turnstileMode]);

  const openConversation = useCallback(async (conversation: ConversationSummary) => {
    const requestId = ++conversationRequest.current;
    setActiveConversationId(conversation.id);
    const conversationModel = availableModelId(conversation.model);
    setSelectedModelId(conversationModel);
    selectedModelRef.current = conversationModel;
    setConversationLoading(true);
    setHistoryOpen(false);
    setError('');
    try {
      const response = await fetch(`/api/ai-chat/conversations/${conversation.id}`, { cache: 'no-store' });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(conversationCopy.loadError);
      if (requestId !== conversationRequest.current) return;
      setMessages((payload?.messages || []).map((message: any) => ({ role: message.role, content: message.content })));
      const loadedModel = availableModelId(payload?.conversation?.model);
      setSelectedModelId(loadedModel);
      selectedModelRef.current = loadedModel;
    } catch (loadError: any) {
      if (requestId === conversationRequest.current) {
        setMessages([]);
        setError(loadError?.message || conversationCopy.loadError);
      }
    } finally {
      if (requestId === conversationRequest.current) setConversationLoading(false);
    }
  }, [availableModelId, conversationCopy.loadError]);

  const createConversation = useCallback(async () => {
    setError('');
    const response = await fetch('/api/ai-chat/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: conversationCopy.defaultTitle, model: selectedModelRef.current }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || !payload?.conversation) throw new Error(conversationCopy.loadError);
    const created = payload.conversation as ConversationSummary;
    setConversations((current) => [created, ...current.filter((item) => item.id !== created.id)]);
    setActiveConversationId(created.id);
    setSelectedModelId(created.model);
    setMessages([]);
    setHistoryOpen(false);
    return created;
  }, [conversationCopy.defaultTitle, conversationCopy.loadError]);

  const loadConversations = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch('/api/ai-chat/conversations', { cache: 'no-store' });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(conversationCopy.loadError);
      const loaded = (payload?.conversations || []) as ConversationSummary[];
      setConversations(loaded);
      const restore = pendingVerificationReturn.current;
      const pendingRetry = pendingChatRequest.current;
      const targetConversationId = pendingRetry?.conversationId || restore?.conversationId;
      const target = targetConversationId
        ? loaded.find((conversation) => conversation.id === targetConversationId)
        : null;
      if (target) await openConversation(target);
      else if (loaded.length > 0) await openConversation(loaded[0]);
      else await createConversation();
      if (restore) {
        setDraft(restore.draft.slice(0, 4000));
        if (isAiChatModelId(restore.model)) {
          const restoredModel = availableModelId(restore.model);
          setSelectedModelId(restoredModel);
          selectedModelRef.current = restoredModel;
        }
        pendingVerificationReturn.current = null;
        try {
          window.sessionStorage.removeItem(HUMAN_VERIFICATION_RETURN_KEY);
        } catch {
          // The restored state only remains in this tab when storage is unavailable.
        }
      }
      if (pendingRetry) {
        if (target?.id === pendingRetry.conversationId) {
          const restoredModel = availableModelId(pendingRetry.model);
          setDraft(pendingRetry.userContent.slice(0, 4000));
          setSelectedModelId(restoredModel);
          selectedModelRef.current = restoredModel;
        } else {
          rememberPendingChatRequest(null);
          setDraft('');
        }
      }
    } catch (loadError: any) {
      setError(loadError?.message || conversationCopy.loadError);
    } finally {
      setHistoryLoading(false);
    }
  }, [availableModelId, conversationCopy.loadError, createConversation, openConversation, rememberPendingChatRequest]);

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem(HUMAN_VERIFICATION_RETURN_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Partial<VerificationReturnState>;
      if (!isAiChatModelId(parsed.model)) return;
      pendingVerificationReturn.current = {
        draft: typeof parsed.draft === 'string' ? parsed.draft.slice(0, 4000) : '',
        model: parsed.model,
        conversationId: typeof parsed.conversationId === 'string' ? parsed.conversationId : null,
      };
      setDraft(pendingVerificationReturn.current.draft);
      setSelectedModelId(parsed.model);
      selectedModelRef.current = parsed.model;
    } catch {
      // Ignore malformed or unavailable storage.
    }
  }, []);

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem(PENDING_CHAT_REQUEST_SESSION_KEY);
      if (!stored) return;
      const parsed = JSON.parse(stored) as Partial<PendingChatRequest>;
      if (typeof parsed.requestId !== 'string'
        || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(parsed.requestId)
        || typeof parsed.conversationId !== 'string'
        || !isAiChatModelId(parsed.model)
        || typeof parsed.userContent !== 'string'
        || !Array.isArray(parsed.messages)) return;
      const restored: PendingChatRequest = {
        requestId: parsed.requestId,
        conversationId: parsed.conversationId,
        model: parsed.model,
        userContent: parsed.userContent.slice(0, 4000),
        messages: parsed.messages
          .filter((message): message is AiChatMessage => !!message
            && (message.role === 'user' || message.role === 'assistant')
            && typeof message.content === 'string')
          .slice(-20),
      };
      if (restored.messages.at(-1)?.role !== 'user') return;
      pendingChatRequest.current = restored;
      setDraft(restored.userContent);
    } catch {
      // Ignore malformed or unavailable retry state.
    }
  }, []);

  useEffect(() => {
    void loadQuota();
  }, [loadQuota]);

  useEffect(() => {
    if (quota?.authenticated
      && (!quota.humanVerification?.required || quota.humanVerification.verified)) {
      void loadConversations();
    }
  }, [loadConversations, quota?.authenticated, quota?.humanVerification]);

  useEffect(() => {
    if (quota?.authenticated
      && quota.humanVerification?.required
      && !quota.humanVerification.verified) {
      redirectForHumanVerification();
    }
  }, [quota?.authenticated, quota?.humanVerification, redirectForHumanVerification]);

  useEffect(() => {
    selectedModelRef.current = selectedModelId;
  }, [selectedModelId]);

  useEffect(() => {
    let introductionComplete = false;
    try {
      introductionComplete = window.sessionStorage.getItem(TURNSTILE_INTRO_SESSION_KEY) === '1';
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }
    turnstileIntroductionCompleteRef.current = introductionComplete;
    setTurnstileIntroductionComplete(introductionComplete);
    setTurnstileSilentMode(introductionComplete);
    setTurnstilePreferenceReady(true);
  }, []);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      setNotice(copy.checkoutSuccess);
      const refreshTimer = window.setTimeout(() => {
        void loadQuota();
      }, 1500);
      return () => window.clearTimeout(refreshTimer);
    }
    if (checkout === 'canceled') setNotice(copy.checkoutCanceled);
  }, [copy.checkoutCanceled, copy.checkoutSuccess, loadQuota, searchParams]);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken('');
    setTurnstileStatus('waiting');
    setTurnstileInteractionRequired(false);
    setTurnstileSilentMode(turnstileIntroductionCompleteRef.current);
    setTurnstileRenderCycle((current) => current + 1);
  }, []);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileStatus('ready');
    setTurnstileInteractionRequired(false);
    setError('');
    if (!turnstileIntroductionCompleteRef.current) {
      turnstileIntroductionCompleteRef.current = true;
      setTurnstileIntroductionComplete(true);
      try {
        window.sessionStorage.setItem(TURNSTILE_INTRO_SESSION_KEY, '1');
      } catch {
        // The current page still switches to silent checks when storage is unavailable.
      }
    }
  }, []);

  const expireTurnstile = useCallback(() => {
    setTurnstileToken('');
    setTurnstileStatus('expired');
    setTurnstileInteractionRequired(true);
  }, []);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, sending]);

  useEffect(() => {
    if (!quota?.authenticated || quota.subscribed || quota.plan !== 'standard' || quota.exhausted) {
      setLowQuotaOpen(false);
      return;
    }
    if (quota.usagePercent < 90) return;

    const warningPeriod = quota.resetAt || new Date().toISOString().slice(0, 10);
    const storageKey = `${LOW_QUOTA_SESSION_KEY}:${warningPeriod}`;
    try {
      if (window.sessionStorage.getItem(storageKey)) return;
      window.sessionStorage.setItem(storageKey, '1');
    } catch {
      // The warning still works when storage is unavailable.
    }
    setLowQuotaOpen(true);
  }, [quota]);

  useEffect(() => {
    if (!lowQuotaOpen) return undefined;
    const closeOnEscape = (event: any) => {
      if (event.key === 'Escape') setLowQuotaOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [lowQuotaOpen]);

  const canSend = !!quota?.authenticated
    && !!activeConversationId
    && !quota.exhausted
    && !syncPendingRequest
    && (isMessageTurnstile ? !!turnstileToken : !!quota.humanVerification?.verified)
    && draft.trim().length > 0
    && !sending
    && !conversationLoading;

  const verificationLabel = turnstileStatus === 'ready'
    ? copy.humanReady
    : turnstileStatus === 'expired'
      ? copy.humanExpired
      : turnstileIntroductionComplete ? copy.humanCheck : copy.humanNeeded;
  const compactTurnstile = turnstileSilentMode && !turnstileInteractionRequired;

  async function sendMessage(event?: FormEvent) {
    event?.preventDefault();
    if (sendingRef.current) return;
    if (!canSend || !activeConversationId) {
      if (isMessageTurnstile && !turnstileToken) setError(copy.humanNeeded);
      else if (!isMessageTurnstile && !quota?.humanVerification?.verified) redirectForHumanVerification();
      return;
    }
    sendingRef.current = true;

    const userMessage: AiChatMessage = { role: 'user', content: draft.trim() };
    const retry = pendingChatRequest.current;
    const canReuseRequest = retry
      && retry.conversationId === activeConversationId
      && retry.model === selectedModelId
      && retry.userContent === userMessage.content;
    const nextMessages = canReuseRequest
      ? retry.messages
      : [...messages, userMessage].slice(-20);
    const pending: PendingChatRequest = canReuseRequest ? retry : {
      requestId: crypto.randomUUID(),
      conversationId: activeConversationId,
      model: selectedModelId,
      userContent: userMessage.content,
      messages: nextMessages,
    };
    rememberPendingChatRequest(pending);
    const token = turnstileToken;
    const conversationId = activeConversationId;
    setMessages(nextMessages);
    setDraft('');
    setSending(true);
    setError('');
    setNotice('');

    try {
      const response = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: pending.requestId,
          conversationId,
          // The server reconstructs trusted context from persisted messages;
          // only the new user message belongs in the request payload.
          messages: [userMessage],
          model: selectedModelId,
          ...(isMessageTurnstile ? { turnstileToken: token } : {}),
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        if (payload?.quota) setQuota((current) => current ? { ...current, ...payload.quota } : current);
        if (response.status === 428 && payload?.code === 'HUMAN_VERIFICATION_REQUIRED') {
          setMessages((current) => current.slice(0, -1));
          setDraft(userMessage.content);
          redirectForHumanVerification({
            draft: userMessage.content,
            model: selectedModelId,
            conversationId,
          });
          return;
        }
        if (payload?.code !== 'AI_CHAT_REQUEST_IN_PROGRESS') rememberPendingChatRequest(null);
        throw new Error(errorCopy(payload?.code, copy));
      }
      if (payload?.replayed && payload?.conversationSaved && payload?.conversation) {
        await openConversation(payload.conversation as ConversationSummary);
      } else {
        setMessages((current) => [...current, payload.message].slice(-200));
      }
      if (payload?.quota) setQuota((current) => current ? { ...current, ...payload.quota } : current);
      if (payload?.syncPending) {
        setSyncPendingRequest(pending);
        setNotice(conversationCopy.saveWarning);
      } else {
        setSyncPendingRequest(null);
        rememberPendingChatRequest(null);
      }
      if (payload?.conversation) {
        setConversations((current) => current
          .map((conversation) => conversation.id === conversationId ? { ...conversation, ...payload.conversation } : conversation)
          .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()));
      }
    } catch (sendError: any) {
      setMessages((current) => current.slice(0, -1));
      setDraft(userMessage.content);
      setError(sendError?.message || copy.generalError);
    } finally {
      sendingRef.current = false;
      setSending(false);
      if (isMessageTurnstile) resetTurnstile();
    }
  }

  async function retryConversationSync() {
    const pending = syncPendingRequest;
    if (!pending || sending || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      const response = await fetch('/api/ai-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: pending.requestId,
          conversationId: pending.conversationId,
          messages: [{ role: 'user', content: pending.userContent }],
          model: pending.model,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.conversationSaved) throw new Error(conversationCopy.saveWarning);
      if (payload.quota) setQuota((current) => current ? { ...current, ...payload.quota } : current);
      setSyncPendingRequest(null);
      rememberPendingChatRequest(null);
      setNotice(conversationCopy.saved);
      if (payload.conversation) await openConversation(payload.conversation as ConversationSummary);
    } catch (syncError: any) {
      setError(syncError?.message || conversationCopy.saveWarning);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void sendMessage();
    }
  }

  async function changeModel(model: AiChatModelId) {
    const requestedModel = getAiChatModel(model);
    if (!requestedModel || sendingRef.current || conversationLoading) return;
    if (!isAiChatModelAvailableForPlan(requestedModel, quota?.plan || 'standard')) {
      setLowQuotaOpen(false);
      setUpgradeModelId(requestedModel.id);
      return;
    }
    setSelectedModelId(model);
    selectedModelRef.current = model;
    if (!activeConversationId) return;
    try {
      const response = await fetch(`/api/ai-chat/conversations/${activeConversationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model }),
      });
      if (!response.ok) throw new Error(copy.generalError);
      setConversations((current) => current.map((conversation) => conversation.id === activeConversationId ? { ...conversation, model } : conversation));
    } catch (modelError: any) {
      setError(modelError?.message || copy.generalError);
    }
  }

  async function saveRename(id: string) {
    const title = editingTitle.trim();
    if (!title) return;
    try {
      const response = await fetch(`/api/ai-chat/conversations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (!response.ok) throw new Error(conversationCopy.loadError);
      setConversations((current) => current.map((conversation) => conversation.id === id ? { ...conversation, title } : conversation));
      setEditingId(null);
    } catch (renameError: any) {
      setError(renameError?.message || conversationCopy.loadError);
    }
  }

  async function startNewConversation() {
    if (activeConversation && messages.length === 0) {
      setDraft('');
      setHistoryOpen(false);
      return;
    }
    try {
      await createConversation();
    } catch (createError: any) {
      setError(createError?.message || conversationCopy.loadError);
    }
  }

  async function deleteConversation(id: string) {
    if (!window.confirm(conversationCopy.confirmDelete)) return;
    try {
      const response = await fetch(`/api/ai-chat/conversations/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(conversationCopy.loadError);
      const remaining = conversations.filter((conversation) => conversation.id !== id);
      setConversations(remaining);
      if (syncPendingRequest?.conversationId === id) {
        setSyncPendingRequest(null);
        rememberPendingChatRequest(null);
      }
      if (activeConversationId === id) {
        if (remaining.length > 0) await openConversation(remaining[0]);
        else await createConversation();
      }
    } catch (deleteError: any) {
      setError(deleteError?.message || conversationCopy.loadError);
    }
  }

  const planLabel = quota?.plan === 'pro' ? 'Pro' : quota?.plan === 'plus' || quota?.subscribed ? 'Plus' : 'Free';
  const usagePercent = Math.min(100, Math.max(0, quota?.usagePercent || 0));
  const modelGroups = useMemo(() => groupAiChatModelsByOrganization(AI_CHAT_TEXT_MODELS), []);
  const upgradeModel = getAiChatModel(upgradeModelId);
  const groupedConversations = useMemo(() => {
    const search = historySearch.trim().toLocaleLowerCase();
    const visible = conversations.filter((conversation) => !search
      || conversation.title.toLocaleLowerCase().includes(search)
      || conversation.preview?.toLocaleLowerCase().includes(search));
    return (['today', 'yesterday', 'previousWeek', 'older'] as const).map((key) => ({
      key,
      label: conversationCopy[key],
      items: visible.filter((conversation) => groupKey(conversation.lastMessageAt) === key),
    })).filter((group) => group.items.length > 0);
  }, [conversationCopy, conversations, historySearch]);

  return (
    <>
      {notice && <div className={styles.chatNotice} role="status"><CheckCircle2 aria-hidden="true" /> {notice}</div>}
      {error && <div className={styles.chatAlert} role="alert">{error}</div>}

      {upgradeModel && (
        <AiChatModelUpgradeDialog
          locale={locale}
          modelName={upgradeModel.name}
          plusMonthlyPriceUsd={plusMonthlyPriceUsd}
          proMonthlyPriceUsd={proMonthlyPriceUsd}
          onClose={() => setUpgradeModelId(null)}
          onNavigate={() => {
            try {
              window.sessionStorage.setItem(HUMAN_VERIFICATION_RETURN_KEY, JSON.stringify({
                draft,
                model: selectedModelId,
                conversationId: activeConversationId,
              }));
            } catch {
              // Checkout remains available when session storage is unavailable.
            }
          }}
        />
      )}

      {lowQuotaOpen && !upgradeModel && (
        <div
          className={styles.chatUpgradeOverlay}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setLowQuotaOpen(false);
          }}
        >
          <section className={styles.chatUpgradeDialog} role="dialog" aria-modal="true" aria-labelledby="chat-upgrade-title">
            <button className={styles.chatUpgradeClose} type="button" onClick={() => setLowQuotaOpen(false)} aria-label={copy.dismiss}><X aria-hidden="true" /></button>
            <span className={styles.chatUpgradeIcon} aria-hidden="true"><Sparkles /></span>
            <span className={styles.chatUpgradeKicker}>{copy.freePlan}</span>
            <h2 id="chat-upgrade-title">{copy.planTitle}</h2>
            <p>{copy.lowQuotaBody}</p>
            <UsageMeter label={copy.freePlan} resetLabel={copy.resetDaily} percent={usagePercent} />
            <div className={styles.chatUpgradeActions}>
              <button type="button" onClick={() => setLowQuotaOpen(false)}>{copy.dismiss}</button>
              <Link href={chatPricingPath} onClick={() => setLowQuotaOpen(false)}>{copy.subscribe}</Link>
            </div>
          </section>
        </div>
      )}

      {quotaLoading ? (
        <div className={styles.chatLoading} role="status"><span aria-hidden="true" />{copy.loadingQuota}</div>
      ) : !quota?.authenticated ? (
        <section className={styles.chatWorkspace}>
          <div className={styles.chatAuthState}>
            <span className={styles.chatStateIcon}><LogIn aria-hidden="true" /></span>
            <h2>{copy.authTitle}</h2>
            <p>{copy.authBody}</p>
            <Link className={styles.primaryButton} href={signInPath}><LogIn aria-hidden="true" /> {copy.signIn}</Link>
          </div>
        </section>
      ) : (
        <div className={styles.chatAppShell}>
          <section className={styles.chatWorkspace} aria-label={copy.chat}>
            <header className={styles.chatToolbar}>
              <div className={styles.chatToolbarIdentity}>
                <button className={styles.chatHistoryToggle} type="button" onClick={() => setHistoryOpen(true)} aria-label={conversationCopy.menu}><Menu aria-hidden="true" /></button>
                <span className={styles.chatOnlineDot} aria-hidden="true" />
                <span>
                  <strong>{activeConversation?.title || conversationCopy.defaultTitle}</strong>
                  <small>{conversationCopy.saved}</small>
                </span>
              </div>
            </header>

            <div className={styles.chatMessages} aria-live="polite">
              {historyLoading || conversationLoading ? (
                <div className={styles.chatConversationLoading}><Loader2 aria-hidden="true" /> {conversationCopy.loading}</div>
              ) : messages.length === 0 ? (
                <div className={styles.chatEmpty}>
                  <span aria-hidden="true">AI</span>
                  <h2>{copy.emptyTitle}</h2>
                  <p>{copy.emptyBody}</p>
                  <div className={styles.chatPromptGrid}>
                    {[copy.emptyBody, copy.entryHint].map((prompt) => <button type="button" key={prompt} onClick={() => setDraft(prompt)}>{prompt}</button>)}
                  </div>
                </div>
              ) : messages.map((message, index) => (
                <article className={`${styles.chatMessage} ${message.role === 'user' ? styles.chatMessageUser : styles.chatMessageAssistant}`} key={`${index}-${message.role}`}>
                  <span className={styles.chatAvatar}>{message.role === 'user' ? <UserRound aria-hidden="true" /> : <Bot aria-hidden="true" />}</span>
                  <div>
                    <strong>{message.role === 'user' ? copy.userLabel : copy.assistantLabel}</strong>
                    {message.role === 'user' ? (
                      <p>{message.content}</p>
                    ) : (
                      <div className={styles.chatMarkdown}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node: _node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" />,
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </article>
              ))}
              {sending && (
                <article className={`${styles.chatMessage} ${styles.chatMessageAssistant}`}>
                  <span className={styles.chatAvatar}><Bot aria-hidden="true" /></span>
                  <div><strong>{copy.assistantLabel}</strong><p className={styles.chatThinking}>{copy.sending}<span aria-hidden="true">...</span></p></div>
                </article>
              )}
              <div ref={messagesEnd} />
            </div>

            <form className={styles.chatComposer} onSubmit={sendMessage}>
              <div className={styles.chatComposerBox}>
                {syncPendingRequest ? (
                  <div className={`${styles.chatQuotaExhausted} ${styles.chatSyncPending}`} role="status">
                    <span className={styles.chatQuotaExhaustedMessage}>
                      <i aria-hidden="true"><Clock3 /></i>
                      <span><strong>{conversationCopy.saveWarning}</strong><small>{conversationCopy.privateHistory}</small></span>
                    </span>
                    <button type="button" onClick={retryConversationSync} disabled={sending}>
                      {sending ? <Loader2 aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />}
                      {sending ? conversationCopy.retryingSave : conversationCopy.retrySave}
                    </button>
                  </div>
                ) : quota.exhausted ? (
                  <div className={styles.chatQuotaExhausted} role="status">
                    <span className={styles.chatQuotaExhaustedMessage}>
                      <i aria-hidden="true"><Clock3 /></i>
                      <span><strong>{copy.quotaReached}</strong><small>{copy.quotaResetTomorrow}</small></span>
                    </span>
                    <Link href={quota.subscribed ? dashboardPath : chatPricingPath}>
                      <Sparkles aria-hidden="true" />
                      {quota.subscribed ? conversationCopy.synced : copy.getMoreQuota}
                      <ArrowRight aria-hidden="true" />
                    </Link>
                  </div>
                ) : (
                  <>
                    <label className="sr-only" htmlFor="ai-chat-message">{copy.placeholder}</label>
                    <textarea
                      id="ai-chat-message"
                      value={draft}
                      onChange={(event) => setDraft(event.target.value.slice(0, 4000))}
                      onKeyDown={handleComposerKeyDown}
                      placeholder={copy.placeholder}
                      rows={2}
                      maxLength={4000}
                      disabled={sending || conversationLoading}
                    />
                    {isMessageTurnstile && turnstilePreferenceReady && (
                      <div className={`${styles.chatVerification} ${compactTurnstile ? styles.chatVerificationCompact : ''}`}>
                        <div>
                          <span>{copy.humanCheck}</span>
                          <small className={turnstileStatus === 'ready' ? styles.verificationReady : undefined} aria-live="polite">{verificationLabel}</small>
                        </div>
                        {turnstileSiteKey ? (
                          <TurnstileWidget
                            siteKey={turnstileSiteKey}
                            action={AI_CHAT_TURNSTILE_ACTION}
                            className={styles.turnstileSlot}
                            appearance={turnstileSilentMode ? 'interaction-only' : 'always'}
                            resetKey={turnstileRenderCycle}
                            onVerify={handleTurnstileVerify}
                            onExpire={expireTurnstile}
                            onError={expireTurnstile}
                            onInteractionChange={setTurnstileInteractionRequired}
                          />
                        ) : <small>{copy.configurationError}</small>}
                      </div>
                    )}
                  </>
                )}
                <footer className={styles.chatComposerFooter}>
                  <div className={styles.chatComposerControls}>
                    <label className={styles.chatModelPicker}>
                      <span>{copy.models}</span>
                      <select
                        value={selectedModelId}
                        onChange={(event) => void changeModel(event.target.value as AiChatModelId)}
                        disabled={sending || conversationLoading}
                        aria-label={`${copy.models}: ${AI_CHAT_TEXT_MODELS.length}`}
                      >
                        {modelGroups.map(({ organization, models }) => (
                          <optgroup key={organization} label={organization}>
                            {models.map((model) => (
                              <option key={model.id} value={model.id}>{model.name}{model.free ? '' : ` · ${modelAccessCopy.paidBadge}`}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      <small className={styles.chatModelCount} aria-hidden="true">{AI_CHAT_TEXT_MODELS.length}</small>
                    </label>
                  </div>
                  <button type="submit" disabled={!canSend} aria-label={copy.send}><Send aria-hidden="true" /><span>{sending ? copy.sending : copy.send}</span></button>
                </footer>
              </div>
              <small className={styles.chatDisclaimer}>{conversationCopy.disclaimer}</small>
            </form>
          </section>

          <button className={`${styles.chatHistoryBackdrop} ${historyOpen ? styles.chatHistoryBackdropOpen : ''}`} type="button" onClick={() => setHistoryOpen(false)} aria-label={copy.clear} />
          <aside className={`${styles.chatHistoryPanel} ${historyOpen ? styles.chatHistoryPanelOpen : ''}`} aria-label={conversationCopy.menu}>
            <header className={styles.chatHistoryHeader}>
              <div className={styles.chatHistoryBrand}><span aria-hidden="true">AI</span><strong>{copy.assistantLabel}</strong></div>
              <button className={styles.chatHistoryClose} type="button" onClick={() => setHistoryOpen(false)} aria-label={copy.clear}><X aria-hidden="true" /></button>
            </header>
            <button className={styles.chatNewConversation} type="button" onClick={() => void startNewConversation()} disabled={sending || conversationLoading}><MessageSquarePlus aria-hidden="true" /> {conversationCopy.newChat}</button>
            <label className={styles.chatHistorySearch}>
              <Search aria-hidden="true" />
              <input type="search" value={historySearch} onChange={(event) => setHistorySearch(event.target.value)} placeholder={conversationCopy.search} />
            </label>
            <div className={styles.chatHistoryList}>
              {groupedConversations.map((group) => (
                <section className={styles.chatHistoryGroup} key={group.key}>
                  <span>{group.label}</span>
                  {group.items.map((conversation) => (
                    <div className={`${styles.chatHistoryItem} ${conversation.id === activeConversationId ? styles.chatHistoryItemActive : ''}`} key={conversation.id}>
                      {editingId === conversation.id ? (
                        <form onSubmit={(event) => { event.preventDefault(); void saveRename(conversation.id); }}>
                          <input value={editingTitle} onChange={(event) => setEditingTitle(event.target.value.slice(0, 120))} autoFocus aria-label={conversationCopy.rename} />
                          <button type="submit" aria-label={conversationCopy.rename}><Check aria-hidden="true" /></button>
                          <button type="button" onClick={() => setEditingId(null)} aria-label={copy.clear}><X aria-hidden="true" /></button>
                        </form>
                      ) : (
                        <>
                          <button className={styles.chatHistoryMain} type="button" onClick={() => void openConversation(conversation)} disabled={sending}>
                            <strong>{conversation.title}</strong>
                            <small>{conversation.preview || conversationCopy.defaultTitle}</small>
                          </button>
                          <span className={styles.chatHistoryActions}>
                            <button type="button" disabled={sending} onClick={() => { setEditingId(conversation.id); setEditingTitle(conversation.title); }} aria-label={conversationCopy.rename}><Pencil aria-hidden="true" /></button>
                            <button type="button" disabled={sending} onClick={() => void deleteConversation(conversation.id)} aria-label={conversationCopy.delete}><Trash2 aria-hidden="true" /></button>
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                </section>
              ))}
            </div>
            <footer className={styles.chatHistoryFooter}>
              <UsageMeter label={planLabel} resetLabel={copy.resetDaily} percent={usagePercent} />
              {quota?.subscribed ? (
                <Link className={`${styles.chatHistoryAccountLink} ${styles.chatHistoryAccountLinkMember}`} href={dashboardPath}>
                  <span className={styles.chatMemberBadge}><Crown aria-hidden="true" />{planLabel} · {copy.memberPlan}</span>
                  <span>{conversationCopy.synced}<ArrowRight aria-hidden="true" /></span>
                </Link>
              ) : (
                <Link className={`${styles.chatHistoryAccountLink} ${styles.chatHistoryAccountLinkFree}`} href={chatPricingPath}>
                  <span><Sparkles aria-hidden="true" />{copy.subscribe}</span>
                  <ArrowRight aria-hidden="true" />
                </Link>
              )}
            </footer>
          </aside>
        </div>
      )}
    </>
  );
}
